import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../components/api/axios";

const PermissionContext = createContext(null);

// Module key aliases for convenience
const KEY_ALIASES = {
  sales: "sales_invoices",
  invoices: "sales_invoices",
  sales_invoices: "sales_invoices",
  bulk_sales: "bulk_sales",
  quotation: "quotations",
  quotations: "quotations",
  inquiry: "inquiries",
  inquiries: "inquiries",
  customer: "customers",
  customers: "customers",
  sale_refund: "sale_refunds",
  sale_refunds: "sale_refunds",
  credit_notes: "credit_notes",
  debit_notes: "debit_notes",
  receipt: "receipts",
  receipts: "receipts",
  payment: "payments",
  payments: "payments",
  purchase_bills: "purchase_bills",
  supplier: "suppliers",
  suppliers: "suppliers",
  journal: "journal_entries",
  journal_entries: "journal_entries",
  contra: "contra_entries",
  contra_entries: "contra_entries",
  bank: "banks",
  banks: "banks",
  coa: "coa",
  bank_reconciliation: "bank_reconciliation",
  cheque_manager: "cheque_manager",
  pos_settlement: "pos_settlement",
  cheque_returns: "cheque_returns",
  pnl_transfer: "pnl_transfer",
  pnl: "report_pnl",
  report_pnl: "report_pnl",
  ledger: "report_ledger",
  report_ledger: "report_ledger",
  trial_balance: "report_trial_balance",
  report_trial_balance: "report_trial_balance",
  balance_sheet: "report_balance_sheet",
  report_balance_sheet: "report_balance_sheet",
  cash_flow: "report_cash_flow",
  report_cash_flow: "report_cash_flow",
  vat: "report_vat",
  report_vat: "report_vat",
  outstanding: "report_outstanding",
  report_outstanding: "report_outstanding",
  expense: "report_expenses",
  expenses: "report_expenses",
  report_expenses: "report_expenses",
  pdc: "report_pdc",
  report_pdc: "report_pdc",
  aging: "report_aging",
  report_aging: "report_aging",
  service: "report_service",
  report_service: "report_service",
  bsp: "report_bsp",
  report_bsp: "report_bsp",
  sales_purchase: "report_sales_purchase",
  report_sales_purchase: "report_sales_purchase",
  tax_report: "report_tax",
  report_tax: "report_tax",
  misc: "report_misc",
  report_misc: "report_misc",
  overview: "report_business_overview",
  business_overview: "report_business_overview",
  report_business_overview: "report_business_overview",
  employee: "employees",
  employees: "employees",
  attendance: "attendance",
  shifts: "shifts",
  timings_leave: "timings_leave",
  whatsapp: "whatsapp",
  company_profile: "company_profile",
  templates: "templates",
  tax_settings: "tax_settings",
  permissions: "permissions",
};

export const PermissionProvider = ({ children }) => {
  const [employees, setEmployees] = useState([]);
  const [activeEmployeeId, setActiveEmployeeId] = useState(() => {
    return localStorage.getItem("active_employee_id") || "";
  });
  const [currentPermissions, setCurrentPermissions] = useState({});
  const [currentRole, setCurrentRole] = useState("Administrator");
  const [isAdmin, setIsAdmin] = useState(true);
  const [hierarchyLevel, setHierarchyLevel] = useState("management");
  const [designationTitle, setDesignationTitle] = useState("CEO / Management");
  const [managerName, setManagerName] = useState("");
  const [teamName, setTeamName] = useState("");
  const [hierarchyScope, setHierarchyScope] = useState({
    can_view_all: true,
    accessible_ids: [],
    subordinate_ids: [],
    team_member_ids: [],
  });
  const [myTeamInfo, setMyTeamInfo] = useState({
    teams: [],
    is_lead_of_any: false,
    total_teammates: 0,
  });
  const [loading, setLoading] = useState(true);

  // Fetch employees list
  const loadEmployees = useCallback(async () => {
    try {
      const res = await api.get("/permissions/employees");
      const list = res.data || [];
      setEmployees(list);
      if (list.length > 0 && !activeEmployeeId) {
        // default to first employee or saved
        const savedId = localStorage.getItem("active_employee_id");
        const chosen = list.find((e) => String(e.id) === savedId) || list[0];
        setActiveEmployeeId(String(chosen.id));
        localStorage.setItem("active_employee_id", String(chosen.id));
      }
    } catch (err) {
      console.warn("Could not load employee permissions list", err);
    }
  }, [activeEmployeeId]);

  // Fetch permissions, hierarchy scope, and team for active employee
  const loadActivePermissions = useCallback(async (empId) => {
    if (!empId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [permRes, scopeRes, teamRes] = await Promise.allSettled([
        api.get(`/permissions/employee/${empId}`),
        api.get(`/permissions/hierarchy/accessible/${empId}`),
        api.get(`/permissions/my-team/${empId}`),
      ]);

      if (permRes.status === "fulfilled" && permRes.value.data) {
        const d = permRes.value.data;
        setCurrentPermissions(d.permissions || {});
        setCurrentRole(d.role_name || "Custom");
        setIsAdmin(Boolean(d.is_admin));
        setHierarchyLevel(d.hierarchy_level || "subordinate");
        setDesignationTitle(d.designation_title || "Staff");
        setManagerName(d.manager_name || "");
        setTeamName(d.team_name || "");
      }

      if (scopeRes.status === "fulfilled" && scopeRes.value.data) {
        setHierarchyScope(scopeRes.value.data);
      }

      if (teamRes.status === "fulfilled" && teamRes.value.data) {
        setMyTeamInfo(teamRes.value.data);
      }
    } catch (err) {
      console.error("Failed to load permissions/hierarchy for employee", empId, err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    if (activeEmployeeId) {
      loadActivePermissions(activeEmployeeId);
    }
  }, [activeEmployeeId, loadActivePermissions]);

  const switchActiveEmployee = (empId) => {
    setActiveEmployeeId(String(empId));
    localStorage.setItem("active_employee_id", String(empId));
  };

  /**
   * Check if employee has permission for a specific module and action.
   * If permissions are not configured or empty, default is true (admin access).
   */
  const can = useCallback(
    (moduleKey, action = "view") => {
      if (isAdmin) return true; // Full admin has access to everything
      if (!currentPermissions || Object.keys(currentPermissions).length === 0) {
        return true;
      }

      const normalizedKey = KEY_ALIASES[moduleKey] || moduleKey;
      const modPerms = currentPermissions[normalizedKey];

      if (!modPerms) {
        // If module not found in config, allow by default
        return true;
      }

      const val = modPerms[action];
      return val === undefined ? true : Boolean(val);
    },
    [isAdmin, currentPermissions]
  );

  /**
   * Check if employee can see records of another employee based on hierarchy:
   * Management / CEO: can see all
   * Manager: can see subordinates
   * Team Manager: can see team members
   * Subordinate: can see self
   */
  const canSeeEmployee = useCallback(
    (targetEmpId) => {
      if (isAdmin || hierarchyScope.can_view_all || hierarchyLevel === "management") {
        return true;
      }
      if (!targetEmpId) return true;
      const tId = Number(targetEmpId);
      const acc = hierarchyScope.accessible_ids || [];
      return acc.includes(tId);
    },
    [isAdmin, hierarchyScope, hierarchyLevel]
  );

  const canView = (moduleKey) => can(moduleKey, "view");
  const canCreate = (moduleKey) => can(moduleKey, "create");
  const canEdit = (moduleKey) => can(moduleKey, "edit");
  const canDelete = (moduleKey) => can(moduleKey, "delete");
  const canExport = (moduleKey) => can(moduleKey, "export");

  const isManagement = isAdmin || hierarchyLevel === "management";
  const isManager = hierarchyLevel === "manager";
  const isTeamManager = hierarchyLevel === "team_manager";
  const isSubordinate = hierarchyLevel === "subordinate";

  const activeEmployee = employees.find((e) => String(e.id) === String(activeEmployeeId)) || null;

  return (
    <PermissionContext.Provider
      value={{
        employees,
        activeEmployeeId,
        activeEmployee,
        currentRole,
        isAdmin,
        hierarchyLevel,
        designationTitle,
        managerName,
        teamName,
        hierarchyScope,
        myTeamInfo,
        isManagement,
        isManager,
        isTeamManager,
        isSubordinate,
        canSeeEmployee,
        currentPermissions,
        loading,
        can,
        canView,
        canCreate,
        canEdit,
        canDelete,
        canExport,
        switchActiveEmployee,
        reloadEmployees: loadEmployees,
        reloadActivePermissions: () => loadActivePermissions(activeEmployeeId),
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    // Return safe default so components never crash even without provider
    return {
      can: () => true,
      canView: () => true,
      canCreate: () => true,
      canEdit: () => true,
      canDelete: () => true,
      canExport: () => true,
      isAdmin: true,
      hierarchyLevel: "management",
      designationTitle: "CEO / Management",
      isManagement: true,
      isManager: false,
      isTeamManager: false,
      isSubordinate: false,
      canSeeEmployee: () => true,
      currentRole: "Administrator",
      activeEmployee: null,
      employees: [],
      hierarchyScope: { can_view_all: true, accessible_ids: [] },
      myTeamInfo: { teams: [] },
      switchActiveEmployee: () => {},
      reloadActivePermissions: () => {},
    };
  }
  return context;
};

export default PermissionContext;
